<template>
  <v-avatar
    :size="size"
    :color="isImage ? undefined : color"
    :class="isImage ? undefined : textClass"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <v-img v-if="isImage" :src="src" :alt="altText" @error="onImageError" />
    <slot v-else name="fallback" :initials="initials">
      <span :style="fallbackStyle">{{ initials }}</span>
    </slot>
  </v-avatar>
</template>

<script lang="ts">
import {
  getUserInitials,
  IUserAvatarSource,
  pickUserAvatarSource,
  pickUserAvatarSourceByTier,
  UserAvatarTier
} from "@/lib/utility/user-avatar";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

/**
 * Renders the profile picture of a user and falls back to the initials when the user has none,
 * or when the picture fails to load. Picks the stored size that fits the rendered size on the
 * current screen density, so a 32 px avatar on a retina display still gets a sharp image, unless
 * one of the variants is asked for by name through its flag.
 *
 * Attributes and listeners that are not props (e.g. `left` inside a chip, `data-test-*`, menu
 * activator bindings) are forwarded to the underlying `v-avatar`.
 */
@Component({ inheritAttrs: false })
export default class UserAvatar extends Vue {
  @Prop({ default: () => ({}) })
  user!: IUserAvatarSource;

  /**
   * Rendered size in px, a Vuetify size string like "24px" works as well.
   */
  @Prop({ default: 32 })
  size!: number | string;

  /**
   * Background color of the initials fallback, the picture has none.
   */
  @Prop({ default: "primary" })
  color!: string;

  /**
   * Text color class of the initials fallback.
   */
  @Prop({ default: "white--text" })
  textClass!: string;

  /**
   * Font size of the initials, inherits from the surrounding text when empty.
   */
  @Prop({ default: "" })
  fontSize!: string;

  /**
   * The stored variants, as flags in the style of the Vuetify size props. Setting one loads that
   * variant instead of the one that fits the rendered size, for placements where the picture may
   * end up scaled up, e.g. on print. The largest flag wins when several are set.
   */
  @Prop({ default: false, type: Boolean })
  thumbnail!: boolean;

  @Prop({ default: false, type: Boolean })
  small!: boolean;

  @Prop({ default: false, type: Boolean })
  medium!: boolean;

  @Prop({ default: false, type: Boolean })
  large!: boolean;

  isImageBroken = false;

  get sizePx(): number {
    return Number.parseInt(String(this.size), 10) || 32;
  }

  get initials(): string {
    return getUserInitials(this.user);
  }

  get fullName(): string {
    return `${this.user?.firstName ?? ""} ${this.user?.lastName ?? ""}`.trim();
  }

  /**
   * Screen readers get the name of the user, or a generic label when the user has none.
   */
  get altText(): string {
    return this.fullName || String(this.$t("components.utility.UserAvatar.alt"));
  }

  /**
   * The variant asked for by name, undefined when the rendered size decides.
   */
  get tier(): UserAvatarTier | undefined {
    if (this.large) {
      return "large";
    }
    if (this.medium) {
      return "medium";
    }
    if (this.small) {
      return "small";
    }
    if (this.thumbnail) {
      return "thumbnail";
    }

    return undefined;
  }

  get src(): string {
    if (this.tier) {
      return pickUserAvatarSourceByTier(this.user?.avatar, this.tier);
    }

    return pickUserAvatarSource(this.user?.avatar, this.sizePx);
  }

  get isImage(): boolean {
    return !!this.src && !this.isImageBroken;
  }

  get fallbackStyle() {
    return this.fontSize ? { fontSize: this.fontSize } : undefined;
  }

  @Watch("src")
  resetBrokenState() {
    this.isImageBroken = false;
  }

  onImageError() {
    this.isImageBroken = true;
  }
}
</script>
